import { useMemo, useState, type ReactNode, type CSSProperties } from 'react';
import { MoneyInput } from './inputs/MoneyInput';
import { formatMoeda, parseMoeda } from '@fiodecobre/shared';

// Calculadora de custo reutilizavel: preco do fornecedor - desconto% + DIFAL%,
// dividido pela quantidade da embalagem = custo por unidade. Componente global
// — usar via <BotaoEstimativaCusto onUsar={...} /> em qualquer tela.

const campoCls = 'border border-gray-300 rounded-lg text-sm px-2 py-1.5';

interface PopupProps {
  onFechar: () => void;
  // Recebe o custo unitario calculado pra preencher o campo de custo da tela.
  onUsar: (custoUnitario: number) => void;
}

// Popup com a calculadora. Overlay proprio (z 1100) pra ficar acima de modais (1000).
export function PopupEstimativaCusto({ onFechar, onUsar }: PopupProps) {
  const [precoFornecedor, setPrecoFornecedor] = useState(0);
  const [descontoPct, setDescontoPct] = useState('');
  const [difalPct, setDifalPct] = useState('4,49');
  const [qtdEmbalagem, setQtdEmbalagem] = useState('1');

  const calc = useMemo(() => {
    const desc = parseMoeda(descontoPct);
    const difal = parseMoeda(difalPct);
    const qtd = parseMoeda(qtdEmbalagem);
    const comDesconto = precoFornecedor * (1 - desc / 100);
    const comDifal = comDesconto * (1 + difal / 100);
    const custoUnit = qtd > 0 ? comDifal / qtd : 0;
    return {
      comDesconto,
      comDifal,
      qtd,
      custoUnit: Math.round(custoUnit * 10000) / 10000,
    };
  }, [precoFornecedor, descontoPct, difalPct, qtdEmbalagem]);

  const pctInput = (valor: string, set: (v: string) => void) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span className="muted">%</span>
      <input
        type="text"
        inputMode="decimal"
        className={campoCls}
        style={{ width: 100, textAlign: 'right' }}
        value={valor}
        placeholder="0"
        onChange={(e) => set(e.target.value)}
      />
    </div>
  );

  const linha = (label: ReactNode, valor: ReactNode) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
      <span>{label}</span>
      {valor}
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8,
      }}
    >
      <div style={{ background: 'var(--panel-bg, #fff)', borderRadius: 10, width: 480, maxWidth: '100%', overflow: 'hidden' }}>
        <div className="modal-header">
          <span style={{ flex: 1 }}>Estimativa de Custo</span>
          <button type="button" className="btn-fechar-modal" onClick={onFechar} aria-label="Fechar">×</button>
        </div>

        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {linha('Preço do fornecedor (embalagem)',
            <MoneyInput valor={precoFornecedor} onChangeValor={setPrecoFornecedor} className={campoCls} style={{ width: 140, textAlign: 'right' }} />,
          )}
          {linha('Desconto', pctInput(descontoPct, setDescontoPct))}

          {linha(
            <span className="muted">Preço com desconto</span>,
            <strong>{formatMoeda(calc.comDesconto, 4)}</strong>,
          )}

          {linha('DIFAL', pctInput(difalPct, setDifalPct))}
          {linha('Quantidade na embalagem',
            <input
              type="text"
              inputMode="decimal"
              className={campoCls}
              style={{ width: 100, textAlign: 'right' }}
              value={qtdEmbalagem}
              onChange={(e) => setQtdEmbalagem(e.target.value)}
            />,
          )}

          {linha(
            <span className="muted">Custo total da embalagem (com DIFAL)</span>,
            <strong>{formatMoeda(calc.comDifal, 4)}</strong>,
          )}

          <div
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: '#fde68a', color: '#000', padding: '10px 12px', borderRadius: 8, marginTop: 4,
            }}
          >
            <strong>Custo por unidade</strong>
            <strong style={{ fontSize: 18 }}>{formatMoeda(calc.custoUnit, 4)}</strong>
          </div>
          {calc.qtd <= 0 && (
            <p className="muted" style={{ fontSize: 12, margin: 0 }}>Informe a quantidade na embalagem (maior que zero).</p>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
            <button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>
            <button
              type="button"
              className="btn-primario"
              disabled={calc.custoUnit <= 0}
              onClick={() => onUsar(calc.custoUnit)}
            >
              Usar como novo custo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface BotaoProps {
  // Chamado com o custo unitario quando o usuario confirma no popup.
  onUsar: (custoUnitario: number) => void;
  label?: string;
  className?: string;
  style?: CSSProperties;
}

// Botao amarelo (padrao de destaque) que abre a calculadora de custo.
export function BotaoEstimativaCusto({ onUsar, label = 'Estimativa de Custo', className, style }: BotaoProps) {
  const [aberto, setAberto] = useState(false);
  return (
    <>
      <button
        type="button"
        className={className ?? 'btn-secundario'}
        style={{ backgroundColor: '#fde68a', color: '#000', border: '1px solid #ca8a04', fontWeight: 600, ...style }}
        onClick={() => setAberto(true)}
      >
        {label}
      </button>
      {aberto && (
        <PopupEstimativaCusto
          onFechar={() => setAberto(false)}
          onUsar={(c) => { onUsar(c); setAberto(false); }}
        />
      )}
    </>
  );
}
